import { Alert, Box, Card, CardContent, Chip, Divider, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import { useErrorDisplay } from '../../components/ErrorDisplay';
import BusySpinner from '../../components/BusySpinner';
import PageTitle from '../../components/PageTitle';
import PanelSegment from '../../components/PanelSegment';
import useViewportMode from '../../hooks/useViewportMode';
import { useAccessInfo } from '../../contexts/AccessTokenProvider';
import { Securables } from '../../Enums';
import ProducerPortalClient from '../../apiClients/ProducerPortalClient';
import { ProducerFinancialHistory, ProducerLedgerAdjustment, ProducerLedgerEntry, ProducerLedgerInvoiceState } from '../../models/producer';
import { Button } from '@mui/material';

const money = (value?: number) =>
    new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format((value || 0) / 100);

const statusColor = (state?: ProducerLedgerInvoiceState): 'default' | 'warning' | 'error' | 'success' => {
    if (state === 'Pending' || state === 'Failed')
        return 'warning';
    if (state === 'Paid')
        return 'success';
    if (state === 'Unpaid')
        return 'default';
    return 'default';
};

const isFailedOrPending = (state?: ProducerLedgerInvoiceState) =>
    state === 'Pending' || state === 'Failed';

const paymentStatusMessage = (state?: ProducerLedgerInvoiceState) => {
    if (state === 'Pending')
        return 'Settlement is pending and payment has not cleared. This period is not payable yet.';
    if (state === 'Failed')
        return 'The linked customer payment failed. Amounts remain non-payable until resolved.';
    if (state === 'Voided')
        return 'The period was voided, likely due to a post-settlement correction.';
    return 'Settlement state is available from the linked customer payment processor.';
};

const StatCard = ({ label, value, helper }: { label: string; value: string; helper?: string }) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
            <Typography variant="overline" color="text.secondary">{label}</Typography>
            <Typography variant="h6" sx={{ mt: 0.75, mb: 0.5 }}>{value}</Typography>
            {helper && <Typography variant="body2" color="text.secondary">{helper}</Typography>}
        </CardContent>
    </Card>
);

const Ledger = ({ entries }: { entries: ProducerLedgerEntry[] }) => <TableContainer component={Card} sx={{ mt: 1.5 }}>
    <Table size="small">
        <TableHead>
            <TableRow>
                <TableCell>Billing period</TableCell>
                <TableCell align="right">Seats</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Gross billed</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Statement</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {entries.length === 0 && (
                <TableRow>
                    <TableCell colSpan={6} sx={{ color: 'text.secondary' }}>No ledger entries yet.</TableCell>
                </TableRow>
            )}
            {entries.map(item => <TableRow key={item.Id}>
                <TableCell>{dayjs(item.BillingPeriodStart).format('MMM D, YYYY')}</TableCell>
                <TableCell align="right">{item.SeatCount}</TableCell>
                <TableCell align="right">{money(item.EffectiveRateCents)}</TableCell>
                <TableCell align="right">{money(item.BilledSeatAmountCents)}</TableCell>
                <TableCell>
                    <Chip size="small" color={statusColor(item.InvoiceState)} label={item.InvoiceState} />
                </TableCell>
                <TableCell>{item.InvoiceId || '-'}</TableCell>
            </TableRow>)}
        </TableBody>
    </Table>
</TableContainer>;

const Adjustments = ({ records }: { records: ProducerLedgerAdjustment[] }) => <TableContainer component={Card} sx={{ mt: 1.5 }}>
    <Table size="small">
        <TableHead>
            <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Note</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {records.length === 0 && (
                <TableRow>
                    <TableCell colSpan={4} sx={{ color: 'text.secondary' }}>No adjustments. This window is clean.</TableCell>
                </TableRow>
            )}
            {records.map(item => <TableRow key={item.Id}>
                <TableCell>{dayjs(item.OccurredDate).format('MMM D, YYYY h:mm a')}</TableCell>
                <TableCell>{item.AdjustmentType}</TableCell>
                <TableCell align="right">{money(item.AmountCents)}</TableCell>
                <TableCell>{item.Note || '-'}</TableCell>
            </TableRow>)}
        </TableBody>
    </Table>
</TableContainer>;

const ProducerReportsPage = () => {
    const history = useHistory();
    const { mode } = useViewportMode();
    const { accessInfo, hasRole } = useAccessInfo();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState(false);
    const [report, setReport] = useState<ProducerFinancialHistory>(null);
    const canView = hasRole(Securables.producer_admin, true);

    const load = async () => {
        if (!accessInfo.selectedProducerCompanyId || !canView)
            return;

        try {
            setBusy(true);
            const financials = await new ProducerPortalClient().GetFinancials(accessInfo.selectedProducerCompanyId);
            setReport(financials);
        } catch (error) {
            setError(error, 'Could not load producer financial reports');
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessInfo.selectedProducerCompanyId, canView]);

    const summary = report?.Summary;
    const nonPayable = isFailedOrPending(summary?.LastPaymentStatus);
    const entries = report?.Entries ?? [];
    const adjustments = report?.Adjustments ?? [];

    return <Box data-viewport-mode={mode}>
        <ErrorDisplay />
        <BusySpinner busy={busy}>
            <PanelSegment
                titleContent={<PageTitle title="Producer financial reports" />}
                toolbar={<Stack direction="row" spacing={1}>
                    <Button variant="text" onClick={() => history.push('/ProducerPortal')}>Dashboard</Button>
                    <Button variant="text" onClick={() => history.push('/ProducerPortal/ClientCompanies')}>Clients</Button>
                </Stack>}
            >
                <Box sx={{ p: 2 }}>
                    {!canView && <Alert severity="warning">Only Producer Admin users can view payout and settlement reports.</Alert>}
                    {canView && <>
                        <Typography variant="h6" sx={{ mb: 1.5 }}>Financial visibility</Typography>
                        {nonPayable && <Alert severity="warning" sx={{ mb: 1.5 }}>{paymentStatusMessage(summary?.LastPaymentStatus)}</Alert>}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
                                <StatCard label="Monthly earned (projected)" value={money(summary?.MonthlyEarningsCents)} />
                            </Box>
                            <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
                                <StatCard label="Projected earnings" value={money(summary?.ProjectedEarningsCents)} />
                            </Box>
                            <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
                                <StatCard label="Pending payout" value={money(summary?.PendingPayoutCents)} />
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                            <Chip size="small" color={statusColor(summary?.LastPaymentStatus)} label={`Settlement: ${summary?.LastPaymentStatus || 'N/A'}`} />
                            <Chip size="small" color={statusColor(summary?.LastPaymentStatus)} label={`Statement: ${summary?.StatementStatus || 'N/A'}`} />
                        </Stack>

                        <Divider sx={{ mt: 2, mb: 1.5 }} />

                        <Typography variant="subtitle1">Ledger history</Typography>
                        <Ledger entries={entries} />

                        <Typography variant="subtitle1" sx={{ mt: 2 }}>Audited adjustments (refunds, chargebacks, settlement corrections)</Typography>
                        <Adjustments records={adjustments} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            Report values come from the linked customer billing settlement stream. The UI never re-calculates earned or payout amounts.
                        </Typography>
                    </>}
                </Box>
                </PanelSegment>
        </BusySpinner>
    </Box>;
};

export default ProducerReportsPage;
